import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Check, ShieldCheck, X } from "lucide-react";
import { Button, Card, PageLoader, Spinner } from "@/components/ui";
import { LegalLinks } from "@/components/Legal";
import { api, ApiError } from "@/lib/api";
import type { ConsentStatus } from "@shared/types";
import { AuthLayout } from "./AuthLayout";

interface ConsentRequest {
  parentName: string;
  familyName: string;
  kids: string[];
  status: ConsentStatus;
}

const COLLECT = [
  "Responsibilities they check off, and jobs they claim and finish",
  "Job ideas they pitch, savings goals, and requests to spend what they've earned",
  "Short notes they add, and changes to their avatar or interests",
  "A PIN to sign in, only on devices you pair",
];
const NEVER = [
  "Contact details, photos, location or school",
  "Ads, selling or sharing their information, or tracking",
  "AI: nothing about your family is sent to an AI service",
  "Chat or public features",
];

/** The link in the COPPA notice email: a parent gives (or declines) consent for their kids. */
export function ConsentPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [done, setDone] = useState<"granted" | "declined" | null>(null);
  const [busy, setBusy] = useState<"yes" | "no" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const req = useQuery<ConsentRequest, ApiError>({
    queryKey: ["consent", token],
    queryFn: () => api.get<ConsentRequest>(`/legal/consent/${encodeURIComponent(token)}`),
    enabled: !!token,
    retry: false,
  });

  async function decide(choice: "yes" | "no") {
    if (choice === "no" && !window.confirm("Your kids' profiles will be deleted and they won't be able to sign in. Continue?")) return;
    setBusy(choice);
    setError(null);
    try {
      await api.post(choice === "yes" ? "/legal/consent" : "/legal/consent/decline", { token });
      setDone(choice === "yes" ? "granted" : "declined");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(null);
    }
  }

  if (token && req.isLoading) return <PageLoader />;
  if (done === "granted") {
    return (
      <AuthLayout title="Thank you!" subtitle="Your consent is recorded, and we've emailed you a confirmation." back="/">
        <Card className="space-y-2">
          <p className="font-extrabold">Next steps</p>
          <p className="text-sm">1. Set each kid's PIN under Family &amp; kids.</p>
          <p className="text-sm">2. Pair each kid's device: Settings → Kid devices → Pair a device.</p>
          <p className="text-sm text-muted">You can withdraw consent anytime under Settings → Privacy &amp; security.</p>
        </Card>
        <Link to="/parent" className="mt-6 block">
          <Button size="lg" block>
            Open the app
          </Button>
        </Link>
      </AuthLayout>
    );
  }
  if (done === "declined") {
    return (
      <AuthLayout title="Got it" subtitle="We deleted your kids' profiles. Nothing will be collected from them." back="/">
        <p className="text-sm text-muted">You can still sign in, or delete your account under Settings → Your data.</p>
      </AuthLayout>
    );
  }
  if (!req.data) {
    return (
      <AuthLayout title="This link has expired" back="/">
        <p className="text-muted">{req.error?.message ?? "Sign in to the app and we'll send you a new one."}</p>
        <Link to="/login" className="mt-6 block">
          <Button size="lg" block>
            Sign in
          </Button>
        </Link>
      </AuthLayout>
    );
  }
  const r = req.data;
  if (r.status === "granted") {
    return (
      <AuthLayout title="Consent already given" subtitle={`${r.familyName} is all set.`} back="/">
        <Link to="/parent">
          <Button size="lg" block>
            Open the app
          </Button>
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Your consent for your kids"
      subtitle={`Hi ${r.parentName}! Before ${r.kids.length ? r.kids.join(", ") : "your kids"} can use Meaningful Rewards in ${r.familyName}, we need your permission as their parent or guardian.`}
      back="/"
    >
      <Card className="space-y-2">
        <p className="font-extrabold">What we'll collect from your kids</p>
        {COLLECT.map((c) => (
          <p key={c} className="flex gap-2 text-sm">
            <Check className="mt-0.5 size-4 shrink-0 text-brand" /> {c}
          </p>
        ))}
        <p className="pt-2 font-extrabold">What we never do</p>
        {NEVER.map((c) => (
          <p key={c} className="flex gap-2 text-sm">
            <X className="mt-0.5 size-4 shrink-0 text-danger" /> {c}
          </p>
        ))}
      </Card>
      <p className="mt-4 flex gap-2 text-sm text-muted">
        <ShieldCheck className="mt-0.5 size-4 shrink-0" />
        We use this only to run your family's economy in the app. Our service providers (Amazon Web Services) host it for us. You can review, download or
        delete your kids' information, or withdraw consent, at any time.
      </p>
      <p className="mt-3 text-sm">
        Please read our{" "}
        <Link to="/privacy#5-childrens-privacy-notice" target="_blank" className="font-bold text-brand underline">
          Children's Privacy Notice
        </Link>{" "}
        before you decide.
      </p>
      {error && <p className="mt-4 text-center text-sm font-semibold text-danger">{error}</p>}
      <div className="mt-6 grid gap-3">
        <Button size="lg" block onClick={() => decide("yes")} loading={busy === "yes"} disabled={!!busy}>
          I'm their parent or guardian, and I consent
        </Button>
        <Button size="lg" block variant="secondary" onClick={() => decide("no")} loading={busy === "no"} disabled={!!busy}>
          I don't consent
        </Button>
      </div>
      <LegalLinks className="mt-8" />
    </AuthLayout>
  );
}

/** The link in an email-confirmation message. */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"working" | "ok" | string>("working");
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    api
      .post("/legal/verify-email", { token })
      .then(() => setState("ok"))
      .catch((err: ApiError) => setState(err.message));
  }, [token]);
  if (state === "working") {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }
  return (
    <AuthLayout title={state === "ok" ? "Email confirmed" : "This link didn't work"} subtitle={state === "ok" ? "Thanks! You're all set." : state} back="/">
      <Link to="/parent">
        <Button size="lg" block>
          Open the app
        </Button>
      </Link>
    </AuthLayout>
  );
}
